import type { RecordingContext } from '@/hooks/useRecordingContext';
import { authenticatedApi } from '@/lib/api/core';
import { logger } from '@/lib/logger';

const PAGE_RECORDINGS_BASE = '/clinical/page-recordings';
/** Matches backend PAGE_RECORDING_AUDIO_MAX_BYTES (~2 hours of typical WebM). */
export const MAX_PAGE_RECORDING_SIZE_BYTES = 200 * 1024 * 1024;
export const DEFAULT_PAGE_RECORDING_TITLE = 'Voice note';
export const PAGE_RECORDING_PAGE_KEYS = {
  consultationsDetail: 'consultations.detail',
  clientProfile: 'client.profile',
  manualUpload: 'page-recordings.upload',
} as const;
export const PAGE_RECORDING_CONTEXT_TYPES = {
  client: 'client',
  profile: 'profile',
  userProfile: 'user_profile',
  consultation: 'consultation',
  visit: 'visit',
  userVisit: 'user_visit',
} as const;

// Mirrors the backend whitelist in
// superclinic/clinical/domains/consultation_media/serializers.py.
// Submitting a context_type outside this set yields 400
// {"context_type": ["Unsupported recording context type."]}.
export const SUPPORTED_PAGE_RECORDING_CONTEXT_TYPES = new Set<string>(
  Object.values(PAGE_RECORDING_CONTEXT_TYPES)
);

export const isSupportedPageRecordingContextType = (value: unknown): value is string =>
  typeof value === 'string' && SUPPORTED_PAGE_RECORDING_CONTEXT_TYPES.has(value);

const ALLOWED_AUDIO_MIME_PREFIXES = [
  'audio/webm',
  // Chrome/Safari often label audio-only WebM (and re-picked voice notes) as video/webm.
  'video/webm',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/mpeg',
  'audio/mp3',
  'audio/mp4',
  'audio/m4a',
  'audio/ogg',
];

const ALLOWED_AUDIO_EXTENSIONS = new Set(['webm', 'wav', 'mp3', 'm4a', 'ogg', 'mpeg']);

const getBlobFileName = (blob: Blob, fallbackName?: string): string => {
  if (fallbackName) return fallbackName;
  if (typeof File !== 'undefined' && blob instanceof File && blob.name) {
    return blob.name;
  }
  return '';
};

const extensionFromFileName = (fileName: string): string => {
  const parts = fileName.toLowerCase().split('.');
  if (parts.length < 2) return '';
  return parts.at(-1) ?? '';
};

/** Map browser MIME/extension quirks to an audio/* type the API accepts. */
export const normalizePageRecordingContentType = (blob: Blob, fileName?: string): string => {
  const rawMime = (blob.type || '').toLowerCase().split(';')[0].trim();
  const resolvedName = getBlobFileName(blob, fileName);
  const extension = extensionFromFileName(resolvedName);

  if (rawMime.startsWith('audio/')) {
    if (rawMime === 'audio/mp3') return 'audio/mpeg';
    if (rawMime === 'audio/m4a') return 'audio/mp4';
    return rawMime;
  }
  if (rawMime === 'video/webm' || extension === 'webm') {
    return 'audio/webm';
  }
  if (extension === 'wav') return 'audio/wav';
  if (extension === 'mp3' || extension === 'mpeg') return 'audio/mpeg';
  if (extension === 'ogg') return 'audio/ogg';
  if (extension === 'm4a' || extension === 'mp4') return 'audio/mp4';
  return rawMime || 'audio/webm';
};

const isAllowedPageRecordingMime = (mimeType: string): boolean => {
  const normalized = mimeType.toLowerCase().split(';')[0].trim();
  if (!normalized || normalized === 'application/octet-stream') {
    // File pickers often omit MIME; rely on extension + signature instead.
    return true;
  }
  return ALLOWED_AUDIO_MIME_PREFIXES.some((mimePrefix) => normalized.startsWith(mimePrefix));
};

export type PageRecording = {
  id?: string;
  recording_id?: string;
  page_recording_id?: string;
  s3_file: string;
  client?: string;
  uploaded_by?: string;
  page_key: string;
  context_type?: string;
  context_id?: string;
  title: string;
  description?: string;
  duration_seconds?: number;
  recorded_at?: string;
  consent_captured?: boolean;
  is_shared_with_client?: boolean;
  auto_transcribe?: boolean;
  workflow_status?: string;
  transcription_status?: string;
  /** Backend flag: show "Transcribe again" when audio can be re-queued. */
  can_retranscribe?: boolean;
  /** Backend flag: show "Convert to English" when stored text can be translated. */
  can_retranslate?: boolean;
  message?: string;
  is_retranscription?: boolean;
  presigned_url?: string;
  created_at?: string;
  updated_at?: string;
  transcript?: {
    raw_transcript?: string;
    edited_transcript?: string;
    transcript_text?: string;
    summary?: string;
    language?: string;
    confirmed_at?: string;
    confirmed_by?: string;
  };
  metadata?: Record<string, unknown>;
};

type PageRecordingPayload = {
  s3_file: string;
  client?: string;
  page_key: string;
  context_type?: string;
  context_id?: string;
  title: string;
  duration_seconds: number;
  recorded_at: string;
  consent_captured: boolean;
  is_shared_with_client: boolean;
  auto_transcribe: boolean;
  metadata: Record<string, unknown>;
};

type UpdatePageRecordingTranscriptPayload = {
  transcript: string;
};

export type PageRecordingListParams = {
  client_id?: string;
  page_key?: string;
  context_type?: string;
  context_id?: string;
  workflow_status?: string;
  transcription_status?: string;
};

/** Strip control/bidi override chars from transcript text shown in the UI. */
export const sanitizeTranscriptDisplayText = (value: string): string => {
  // C0/C1 controls + bidi isolates/overrides that can spoof UI layout.
  // Built without literal control escapes so eslint no-control-regex stays clean.
  const controlChars = new RegExp(
    `[${String.fromCharCode(0)}-${String.fromCharCode(8)}${String.fromCharCode(11)}${String.fromCharCode(12)}${String.fromCharCode(14)}-${String.fromCharCode(31)}${String.fromCharCode(127)}-${String.fromCharCode(159)}]`,
    'g'
  );
  const bidiChars = /[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g;
  return value.normalize('NFC').replace(controlChars, '').replace(bidiChars, '');
};

export const getPageRecordingTranscriptText = (recording: PageRecording) => {
  const raw = recording.transcript?.transcript_text || recording.transcript?.raw_transcript || '';
  return raw ? sanitizeTranscriptDisplayText(raw) : '';
};

export const isPageRecordingProcessing = (recording: PageRecording) =>
  (recording.transcription_status || '').toLowerCase() === 'processing';

export const isPageRecordingTranscriptEnglish = (recording: PageRecording) => {
  const language = (recording.transcript?.language || '').toLowerCase();
  if (!language) {
    return Boolean(getPageRecordingTranscriptText(recording));
  }
  return language === 'en' || language.startsWith('en-');
};

/** Prefer backend flag; fall back for older API responses. */
export const canRetranscribePageRecording = (recording: PageRecording) => {
  if (typeof recording.can_retranscribe === 'boolean') {
    return recording.can_retranscribe;
  }
  return Boolean(recording.s3_file) && !isPageRecordingProcessing(recording);
};

/** Prefer backend flag; fall back when transcript text exists. */
export const canRetranslatePageRecording = (recording: PageRecording) => {
  if (typeof recording.can_retranslate === 'boolean') {
    return recording.can_retranslate;
  }
  return Boolean(getPageRecordingTranscriptText(recording));
};

type PresignedUploadResponse = {
  upload_url: string;
  file_id: string;
  key: string;
  upload_headers?: Record<string, string>;
};

export class PageRecordingApiError extends Error {
  readonly status?: number;
  readonly code?: string;

  constructor(message: string, options?: { status?: number; code?: string }) {
    super(message);
    this.name = 'PageRecordingApiError';
    this.status = options?.status;
    this.code = options?.code;
  }
}

export const getPageRecordingId = (recording: Partial<PageRecording>): string | null =>
  // Prefer recording_id (canonical PK), then id aliases used by older payloads.
  recording.recording_id ?? recording.id ?? recording.page_recording_id ?? null;

const unwrapPageRecordingPayload = (payload: unknown): Record<string, unknown> | null => {
  if (!payload || typeof payload !== 'object') return null;
  const typedPayload = payload as Record<string, unknown>;
  if (typedPayload.data && typeof typedPayload.data === 'object') {
    return typedPayload.data as Record<string, unknown>;
  }
  if (typedPayload.result && typeof typedPayload.result === 'object') {
    return typedPayload.result as Record<string, unknown>;
  }
  if (typedPayload.page_recording && typeof typedPayload.page_recording === 'object') {
    return typedPayload.page_recording as Record<string, unknown>;
  }
  return typedPayload;
};

const parsePageRecording = (payload: unknown): PageRecording => {
  const unwrappedPayload = unwrapPageRecordingPayload(payload);
  if (!unwrappedPayload) {
    return {} as PageRecording;
  }
  const parsedRecording = unwrappedPayload as PageRecording;
  const canonicalId = getPageRecordingId(parsedRecording);
  if (!canonicalId) {
    logger.warn('page recording payload missing canonical id');
    return parsedRecording;
  }
  return {
    ...parsedRecording,
    id: canonicalId,
    recording_id: canonicalId,
    page_recording_id: canonicalId,
  };
};

const parsePageRecordingList = (payload: unknown): PageRecording[] => {
  if (Array.isArray(payload)) {
    return payload.map(parsePageRecording);
  }
  if (!payload || typeof payload !== 'object') {
    return [];
  }
  const typedPayload = payload as Record<string, unknown>;
  const listPayload =
    (Array.isArray(typedPayload.results) && typedPayload.results) ||
    (Array.isArray(typedPayload.data) && typedPayload.data) ||
    (Array.isArray(typedPayload.items) && typedPayload.items) ||
    [];
  return listPayload.map(parsePageRecording);
};

const sanitizeMetadataValue = (value: unknown): string | number | boolean | null => {
  if (typeof value === 'string') {
    return value.slice(0, 500);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  return null;
};

export const sanitizePageRecordingMetadata = (metadata?: Record<string, unknown>) => {
  if (!metadata || typeof metadata !== 'object') {
    return {} as Record<string, string | number | boolean | null>;
  }
  return Object.entries(metadata)
    .slice(0, 20)
    .reduce(
      (accumulator, [key, value]) => {
        const sanitizedKey = key.replace(/[^a-zA-Z0-9_.-]/g, '').slice(0, 100);
        if (!sanitizedKey) {
          return accumulator;
        }
        accumulator[sanitizedKey] = sanitizeMetadataValue(value);
        return accumulator;
      },
      {} as Record<string, string | number | boolean | null>
    );
};

type ApiErrorLike = {
  message?: string;
  code?: string;
  response?: {
    status?: number;
    statusText?: string;
    data?: { detail?: string; message?: string } | string;
  };
};

const isApiErrorLike = (error: unknown): error is ApiErrorLike =>
  typeof error === 'object' &&
  error !== null &&
  ('message' in error || 'response' in error || 'code' in error);

const sanitizeApiErrorForLog = (error: unknown) => {
  if (!isApiErrorLike(error)) {
    return { message: 'Request failed' };
  }
  return {
    message: error.message ?? 'Request failed',
    status: error.response?.status,
    statusText: error.response?.statusText,
    code: error.code,
  };
};

const normalizePageRecordingApiError = (
  error: unknown,
  defaultMessage: string
): PageRecordingApiError => {
  if (!isApiErrorLike(error)) {
    return new PageRecordingApiError(defaultMessage);
  }
  const status = error.response?.status;
  const detailMessage =
    typeof error.response?.data === 'string'
      ? error.response.data
      : error.response?.data?.detail || error.response?.data?.message;
  const message = detailMessage || error.message || defaultMessage;
  return new PageRecordingApiError(message, {
    status,
    code: error.code,
  });
};

const startsWithBytes = (bytes: Uint8Array, expected: number[]) =>
  expected.every((value, index) => bytes[index] === value);

const isValidAudioSignature = (bytes: Uint8Array) => {
  if (bytes.length < 12) return false;
  const isWebm = startsWithBytes(bytes, [0x1a, 0x45, 0xdf, 0xa3]);
  const isWav =
    startsWithBytes(bytes, [0x52, 0x49, 0x46, 0x46]) &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x41 &&
    bytes[10] === 0x56 &&
    bytes[11] === 0x45;
  const isMp3Id3 = startsWithBytes(bytes, [0x49, 0x44, 0x33]);
  const isMp3Frame = bytes[0] === 0xff && (bytes[1] & 0xe0) === 0xe0;
  const isOgg = startsWithBytes(bytes, [0x4f, 0x67, 0x67, 0x53]);
  const isMp4Family =
    bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70;
  return isWebm || isWav || isMp3Id3 || isMp3Frame || isOgg || isMp4Family;
};

export const validatePageRecordingBlob = async (blob: Blob, fileName?: string): Promise<void> => {
  if (blob.size > MAX_PAGE_RECORDING_SIZE_BYTES) {
    throw new Error('Recording exceeds 200MB limit');
  }

  const resolvedName = getBlobFileName(blob, fileName);
  const extension = extensionFromFileName(resolvedName);
  const mimeAllowed = isAllowedPageRecordingMime(blob.type || '');
  const extensionAllowed = !extension || ALLOWED_AUDIO_EXTENSIONS.has(extension);

  if (!mimeAllowed || !extensionAllowed) {
    throw new Error('Only audio recording formats are allowed');
  }

  const signatureBytes = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
  if (!isValidAudioSignature(signatureBytes)) {
    throw new Error('Invalid audio file signature');
  }
};

export const getPageRecordingUploadUrl = async (
  fileName: string,
  contentType: string,
  size: number
): Promise<PresignedUploadResponse> => {
  try {
    const { data } = await authenticatedApi.post<PresignedUploadResponse>(
      `${PAGE_RECORDINGS_BASE}/presigned-upload`,
      {
        file_name: fileName,
        content_type: contentType,
        size,
      }
    );
    return data;
  } catch (error) {
    throw normalizePageRecordingApiError(error, 'Could not prepare upload URL');
  }
};

export const uploadPageRecordingBlob = async (
  uploadUrl: string,
  blob: Blob,
  uploadHeaders?: Record<string, string>,
  fileName?: string
): Promise<void> => {
  await validatePageRecordingBlob(blob, fileName);
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: uploadHeaders,
      body: blob,
    });
    if (!response.ok) {
      throw new PageRecordingApiError('Upload request failed', { status: response.status });
    }
  } catch (error) {
    if (error instanceof PageRecordingApiError) {
      throw error;
    }
    throw normalizePageRecordingApiError(error, 'Audio upload failed');
  }
};

export const createPageRecording = async (
  payload: PageRecordingPayload
): Promise<PageRecording> => {
  try {
    // Backend rejects context_type values outside the supported whitelist
    // with 400. When the page hasn't supplied a recognised context_type,
    // drop both fields so the API treats the recording as having no
    // specific context (page_key is preserved for downstream routing).
    const candidateContextType = payload.context_type?.trim() ?? '';
    const includeContextFields =
      candidateContextType.length > 0 &&
      SUPPORTED_PAGE_RECORDING_CONTEXT_TYPES.has(candidateContextType);
    const sanitizedPayload: PageRecordingPayload = {
      ...payload,
      context_type: includeContextFields ? candidateContextType : '',
      context_id: includeContextFields ? (payload.context_id ?? '') : '',
      metadata: sanitizePageRecordingMetadata(payload.metadata),
    };
    const response = await authenticatedApi.post(PAGE_RECORDINGS_BASE, sanitizedPayload);
    return parsePageRecording(response.data);
  } catch (error: unknown) {
    logger.error('recording create failed', sanitizeApiErrorForLog(error));
    throw normalizePageRecordingApiError(error, 'Could not save page recording');
  }
};

export const listPageRecordings = async (
  params?: PageRecordingListParams
): Promise<PageRecording[]> => {
  const response = await authenticatedApi.get(PAGE_RECORDINGS_BASE, { params: params ?? {} });
  return parsePageRecordingList(response.data);
};

export const getPageRecordingPlayback = async (
  recordingId: string,
  options?: { signal?: AbortSignal }
) => {
  const response = await authenticatedApi.get<{ playback_url: string }>(
    `${PAGE_RECORDINGS_BASE}/${recordingId}/playback`,
    options?.signal ? { signal: options.signal } : undefined
  );
  return response.data;
};

export type PageRecordingDownloadResponse = {
  download_url: string;
  filename: string;
  content_type: string;
};

export const getPageRecordingDownload = async (
  recordingId: string
): Promise<PageRecordingDownloadResponse> => {
  try {
    const response = await authenticatedApi.get<PageRecordingDownloadResponse>(
      `${PAGE_RECORDINGS_BASE}/${recordingId}/download`
    );
    return response.data;
  } catch (error) {
    throw normalizePageRecordingApiError(error, 'Could not prepare download URL');
  }
};

/** Open the attachment-disposition S3 URL so the browser saves the audio file.
 * Lives next to the download API helper so callers don't reimplement anchor clicks.
 */
export const downloadPageRecordingFile = async (recordingId: string): Promise<void> => {
  const { download_url, filename } = await getPageRecordingDownload(recordingId);
  const link = document.createElement('a');
  link.href = download_url;
  link.download = filename || 'recording.webm';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getPageRecordingTranscript = async (recordingId: string) => {
  const response = await authenticatedApi.get(`${PAGE_RECORDINGS_BASE}/${recordingId}/transcript`);
  return response.data;
};

export const deletePageRecording = async (recordingId: string): Promise<void> => {
  await authenticatedApi.delete(`${PAGE_RECORDINGS_BASE}/${recordingId}`);
};

export const transcribePageRecording = async (
  recordingId: string,
  options?: { force?: boolean }
): Promise<PageRecording> => {
  const response = await authenticatedApi.post(
    `${PAGE_RECORDINGS_BASE}/${recordingId}/transcribe`,
    { force: options?.force ?? true }
  );
  return parsePageRecording(response.data);
};

/** Re-run AWS Transcribe from audio; stores English transcript. */
export const retranscribePageRecording = async (recordingId: string): Promise<PageRecording> => {
  const response = await authenticatedApi.post(
    `${PAGE_RECORDINGS_BASE}/${recordingId}/retranscribe`
  );
  return parsePageRecording(response.data);
};

/** Translate existing non-English transcript text to English (no audio job). */
export const retranslatePageRecording = async (recordingId: string): Promise<PageRecording> => {
  const response = await authenticatedApi.post(
    `${PAGE_RECORDINGS_BASE}/${recordingId}/retranslate`
  );
  return parsePageRecording(response.data);
};

export const updatePageRecordingTranscript = async (
  recordingId: string,
  payload: UpdatePageRecordingTranscriptPayload
) => {
  const response = await authenticatedApi.patch(
    `${PAGE_RECORDINGS_BASE}/${recordingId}/transcript`,
    payload
  );
  return response.data;
};

export const saveVoiceRecording = async (
  blob: Blob,
  durationSeconds: number,
  context: RecordingContext
) => {
  await validatePageRecordingBlob(blob);
  const normalizedContentType = normalizePageRecordingContentType(blob);
  const fileName = `voice-note-${Date.now()}.webm`;
  const uploadMeta = await getPageRecordingUploadUrl(fileName, normalizedContentType, blob.size);

  await uploadPageRecordingBlob(uploadMeta.upload_url, blob, uploadMeta.upload_headers);

  return createPageRecording({
    s3_file: uploadMeta.file_id,
    client: context.client,
    page_key: context.page_key,
    context_type: context.context_type ?? '',
    context_id: context.context_id ?? '',
    title: context.title ?? DEFAULT_PAGE_RECORDING_TITLE,
    duration_seconds: durationSeconds,
    recorded_at: new Date().toISOString(),
    consent_captured: true,
    is_shared_with_client: false,
    auto_transcribe: true,
    metadata: sanitizePageRecordingMetadata(context.metadata),
  });
};

const extensionForAudioMime = (mimeType: string): string => {
  const normalized = mimeType.toLowerCase();
  if (normalized.includes('wav')) return 'wav';
  if (normalized.includes('mpeg') || normalized.includes('mp3')) return 'mp3';
  if (normalized.includes('ogg')) return 'ogg';
  if (normalized.includes('mp4') || normalized.includes('m4a')) return 'm4a';
  return 'webm';
};

/**
 * Upload an audio file from the page-recordings library UI (file picker).
 * Uses the clinical presigned-upload endpoint, then creates the recording row.
 */
export const uploadPageRecordingFromFile = async (
  file: File,
  options: {
    client: string;
    title?: string;
    auto_transcribe?: boolean;
    page_key?: string;
  }
): Promise<PageRecording> => {
  await validatePageRecordingBlob(file, file.name);
  const contentType = normalizePageRecordingContentType(file, file.name);

  const extension = extensionForAudioMime(contentType);
  const safeBaseName = file.name.replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '_') || 'recording';
  const fileName = `${safeBaseName}-${Date.now()}.${extension}`;
  const uploadMeta = await getPageRecordingUploadUrl(fileName, contentType, file.size);
  await uploadPageRecordingBlob(uploadMeta.upload_url, file, uploadMeta.upload_headers, file.name);

  return createPageRecording({
    s3_file: uploadMeta.file_id,
    client: options.client,
    page_key: options.page_key ?? PAGE_RECORDING_PAGE_KEYS.manualUpload,
    context_type: PAGE_RECORDING_CONTEXT_TYPES.client,
    context_id: options.client,
    title: options.title?.trim() || file.name || DEFAULT_PAGE_RECORDING_TITLE,
    duration_seconds: 0,
    recorded_at: new Date().toISOString(),
    consent_captured: true,
    is_shared_with_client: false,
    auto_transcribe: options.auto_transcribe ?? true,
    metadata: sanitizePageRecordingMetadata({
      source: 'manual_upload',
      original_filename: file.name,
      original_content_type: file.type || null,
    }),
  });
};
